import { useState, useCallback, useEffect } from 'react';
import { FiPlusCircle, FiMinusCircle } from 'react-icons/fi';

/**
 * BroadcastTreeSidebar
 *
 * Renders the broadcast filter tree returned by GET /broadcasts/filter-tree.
 *
 * Tree shape:
 *   years[]
 *     .year, .broadcastCount
 *     months[]
 *       .month, .monthName, .broadcastCount
 *       weeks[]
 *         .week, .dayRange, .broadcastCount
 *         broadcasts[]
 *           ._id, .titre, .speaker_name, .location, .url, .date_scheduled
 *
 * Props:
 *   filterTree      – { years[], speakers[], locations[] } from the API
 *   filters         – current active filters { annee, mois, semaine }
 *   selectedVideo   – currently selected broadcast object (for highlight)
 *   onFilterSelect  – callback({ annee?, mois?, semaine?, _video? })
 *   onClearFilters  – callback() when "Tout afficher" is clicked
 *   className       – extra CSS class for the <aside>
 */
export default function BroadcastTreeSidebar({
  filterTree = {},
  filters = {},
  selectedVideo = null,
  onFilterSelect,
  onClearFilters,
  className = '',
}) {
  const [openSections, setOpenSections] = useState({ byYear: false });
  const [openYears, setOpenYears] = useState({});
  const [openMonths, setOpenMonths] = useState({});
  const [openWeeks, setOpenWeeks] = useState({});
  const [hasTreeSelection, setHasTreeSelection] = useState(false);

  const toggle = (setObj, key) =>
    setObj(prev => ({ ...prev, [key]: !prev[key] }));

  // Collapse everything when filters are cleared externally
  const filtersAnnee = filters.annee;
  const filtersMois = filters.mois;
  const selectedId = selectedVideo?.id ?? selectedVideo?._id ?? null;
  useEffect(() => {
    if (!filtersAnnee && !filtersMois && !selectedId) {
      setOpenYears({});
      setOpenMonths({});
      setOpenWeeks({});
      setHasTreeSelection(false);
    }
  }, [filtersAnnee, filtersMois, selectedId]);

  const handleFilterClick = useCallback((newFilters) => {
    setHasTreeSelection(true);
    if (onFilterSelect) onFilterSelect(newFilters);
  }, [onFilterSelect]);

  const handleClear = useCallback(() => {
    setOpenYears({});
    setOpenMonths({});
    setOpenWeeks({});
    setHasTreeSelection(false);
    if (onClearFilters) onClearFilters();
  }, [onClearFilters]);

  const years = filterTree.years ?? [];

  const renderTree = () => {
    if (years.length === 0) {
      return (
        <div style={{ padding: '8px 12px', color: '#aaa', fontSize: 13 }}>
          Aucun direct disponible.
        </div>
      );
    }

    return years.map(yData => {
      const year = yData.year;
      const isYearOpen = !!openYears[year];
      const isYearSelected =
        String(filters.annee) === String(year) && !filters.mois;

      return (
        <div key={year}>
          {/* ── Year row ── */}
          <div
            className={`tree-year${isYearOpen ? ' open' : ''}${isYearSelected ? ' selected' : ''}`}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div
              onClick={() => toggle(setOpenYears, year)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="icon">{isYearOpen ? '−' : '+'}</span>
              {year}
              {yData.broadcastCount != null && (
                <span style={{ fontSize: 11, color: '#aaa', marginLeft: 2 }}>
                  ({yData.broadcastCount})
                </span>
              )}
            </div>
          </div>

          {/* ── Months ── */}
          {isYearOpen && (yData.months ?? []).map(mData => {
            const mKey = `${year}-${mData.month}`;
            const isMonthOpen = !!openMonths[mKey];
            const isMonthSelected =
              String(filters.annee) === String(year) &&
              String(filters.mois) === String(mData.month);

            return (
              <div key={mKey}>
                <div
                  className={`tree-month${isMonthOpen ? ' open' : ''}${isMonthSelected ? ' selected' : ''}`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div onClick={() => toggle(setOpenMonths, mKey)}
                  className='d-flex align-items-center'
                    style={{ flex: 1, gap: 4 }}>
                    <span className="icon">{isMonthOpen ? '−' : '+'}</span>
                    {mData.monthName}
                    {mData.broadcastCount != null && (
                      <span style={{ fontSize: 11, color: '#aaa', marginLeft: 2 }}>
                        ({mData.broadcastCount})
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Weeks ── */}
                {isMonthOpen && (mData.weeks ?? []).map(wData => {
                  const wKey = `${mKey}-w${wData.week}`;
                  const isWeekOpen = !!openWeeks[wKey];
                  const isWeekSelected = 
                    String(filters.annee) === String(year) &&
                    String(filters.mois) === String(mData.month) &&
                    String(filters.semaine) === String(wData.week);

                  return (
                    <div key={wKey}>
                      <div
                        className={`tree-week d-flex justify-content-between align-items-center ${isWeekOpen ? ' open' : ''}${isWeekSelected ? ' selected' : ''}`}>
                        <div
                          onClick={() => toggle(setOpenWeeks, wKey)}
                          className='d-flex align-items-center'
                          style={{ flex: 1, gap: 4 }}>
                          <span className="icon">{isWeekOpen ? '−' : '+'}</span>
                          {wData.dayRange ? `${wData.dayRange} ${mData.monthName}` : `Sem. ${wData.week}`}
                          {wData.broadcastCount != null && (
                            <span style={{ fontSize: 11, color: '#aaa', marginLeft: 2 }}>
                              ({wData.broadcastCount})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ── Broadcasts ── */}
                      {isWeekOpen && (wData.broadcasts ?? []).map(b => {
                        const isSelected =
                          selectedVideo &&
                          (selectedVideo._id === b._id || selectedVideo.id === b._id);
                        return (
                          <div
                            key={b._id}
                            className={`tree-day${isSelected ? ' selected' : ''}`}
                            onClick={() =>
                              handleFilterClick({
                                annee: String(year),
                                mois: String(mData.month),
                                semaine: String(wData.week),
                                _videoId: b._id,
                                _video: b,
                              })
                            }
                            title={b.speaker_name ?? ''}>
                            {b.titre ?? 'Direct'}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <aside className={`sidebar-col ${className}`}>
      {/* Clear / reset button */}
      {hasTreeSelection && (
        <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #eee', marginBottom: 8 }}>
          <button
            onClick={handleClear}
            className='d-flex align-items-center justify-content-center'
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'var(--mcm-light)',
              color: 'var(--mcm)',
              border: 'none',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              gap: 6,
            }}>
            Tout afficher / Réinitialiser
          </button>
        </div>
      )}

      {/* DIRECTS PAR ANNÉE section */}
      <div className="sidebar-section">
        <button
          className={`sidebar-section-btn${openSections.byYear ? ' open' : ''}`}
          onClick={() => toggle(setOpenSections, 'byYear')}
        >
          <span className="icon" style={{ marginRight: 8, display: 'inline-flex' }}>
            {openSections.byYear ? <FiMinusCircle size={16} /> : <FiPlusCircle size={16} />}
          </span>
          DIRECTS PAR ANNÉE
        </button>
        {openSections.byYear && (
          <div className="sidebar-tree">{renderTree()}</div>
        )}
      </div>
    </aside>
  );
}
